import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getClientAuth, getClientStorage } from '@canvas/firebase-config/client';
import { STORAGE_PATHS } from '@canvas/firebase-config/config';

const MAX_BYTES = 12 * 1024 * 1024; // 12 MB

export type UploadSource = 'storage' | 'local';

export interface UploadResult {
  url: string;
  path: string | null;
  source: UploadSource;
  name: string;
}

function sanitizeFilename(name: string): string {
  const cleaned = name
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  return (cleaned || 'image').slice(0, 80);
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Upload an image to Firebase Storage under the project path.
 * Falls back to a local data URL when unauthenticated or Storage fails.
 */
export async function uploadProjectImage(options: {
  file: File;
  projectId: string;
}): Promise<UploadResult> {
  const { file, projectId } = options;

  if (!file.type.startsWith('image/')) {
    throw new Error('Only image files are supported');
  }
  if (file.size > MAX_BYTES) {
    throw new Error('Image must be 12 MB or smaller');
  }

  const name = file.name || 'image';
  const auth = getClientAuth();
  const userId = auth.currentUser?.uid;

  if (!userId) {
    const url = await readAsDataUrl(file);
    return { url, path: null, source: 'local', name };
  }

  try {
    const storage = getClientStorage();
    const safe = sanitizeFilename(name);
    const path = `${STORAGE_PATHS.projectUploads(userId, projectId)}/${Date.now()}_${safe}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file, {
      contentType: file.type || 'image/png',
      customMetadata: {
        originalName: name.slice(0, 120),
        projectId,
      },
    });
    const url = await getDownloadURL(storageRef);
    return { url, path, source: 'storage', name };
  } catch (err) {
    console.warn('Storage upload failed, using local data URL', err);
    const url = await readAsDataUrl(file);
    return { url, path: null, source: 'local', name };
  }
}
